/* eslint-disable @typescript-eslint/no-explicit-any */
import { InferenceSession, type Tensor } from "onnxruntime-web";

const MODEL_PATH = "./model.onnx";
// const MODEL_PATH = "./model-nms-int32.onnx";
// const MODEL_PATH = "./yolov8n-int32.onnx";

export class Detector {
  private session!: InferenceSession;
  private onReadyPromise;
  private resolveOnReady!: () => void;
  private rejectOnReady!: () => void;
  constructor(modelPath: string = MODEL_PATH) {
    this.onReadyPromise = new Promise<void>((resolve, reject) => {
      this.resolveOnReady = resolve;
      this.rejectOnReady = reject;
    });
    InferenceSession.create(modelPath, { executionProviders: ["wasm"] })
      .then((session) => {
        this.session = session;
        this.resolveOnReady();
      })
      .catch(() => this.rejectOnReady());
  }

  onReady() {
    return this.onReadyPromise;
  }

  async detect(tensor: Tensor, originalSize: { width: number; height: number }) {
    const session = this.session;
    const feeds = {};
    // @ts-ignore
    feeds[session.inputNames[0]] = tensor;

    const outputData = await session.run(feeds);
    const output = outputData[session.outputNames[0]]; // Tensor[1,5,8400]

    const index = outputData["num_selected_indices"].data[2];

    if (index === undefined) {
      return null;
    }

    const originalDimensions = [originalSize.height, originalSize.width];
    const resizedDimensions = [640, 640];

    let box = this.getNthImage(output, Number(index));
    box = scale_xywh(box, 1.05);

    box = xywh2xyxy(box);
    box = resize_to_original_boxes([box], resizedDimensions, originalDimensions)[0];

    const xywhBox = xyxy2xywh(box);
    const [left, top, width, height] = xywhBox;

    return {
      left: Math.max(0, Math.floor(left) - 5),
      top: Math.max(0, Math.floor(top) - 5),
      width: Math.ceil(width) + 10,
      height: Math.ceil(height) + 10,
    };
  }

  private getNthImage(tensor: Tensor, index: number) {
    const l = tensor.dims[1];
    const boxesDim = tensor.dims[2];
    const result = [];
    for (let i = 0; i < l; i++) {
      result.push(tensor.data[index + i * boxesDim]);
    }
    return result;
  }
}

function scale_xywh(box: any, ratio: number) {
  const [x, y, w, h, ...rest] = box;
  return [x, y, w * ratio, h * ratio, ...rest];
}

/**
 * Convert center, width, height to x1, y1, x2, y2
 * @param box
 * @returns {*[]}
 */
function xywh2xyxy(box: any) {
  const [x, y, w, h, ...rest] = box;
  return [x - w / 2, y - h / 2, x + w / 2, y + h / 2, ...rest];
}

function xyxy2xywh(box: any) {
  const [x1, y1, x2, y2, ...rest] = box;
  return [x1, y1, x2 - x1, y2 - y1, ...rest];
}

function resize_to_original_boxes(boxes: any, scaledImageShape: any, origImageShape: any) {
  const gain = Math.min(scaledImageShape[0] / origImageShape[0], scaledImageShape[1] / origImageShape[1]);
  const pad = [
    (scaledImageShape[1] - origImageShape[1] * gain) / 2,
    (scaledImageShape[0] - origImageShape[0] * gain) / 2,
  ];
  return boxes.map((box: any) => {
    const copy = [...box];
    copy[0] = (box[0] - pad[0]) / gain;
    copy[2] = (box[2] - pad[0]) / gain;
    copy[1] = (box[1] - pad[1]) / gain;
    copy[3] = (box[3] - pad[1]) / gain;

    return copy;
  });
}
